import React, { createContext, useContext, useCallback, useState, ReactNode } from 'react';
import { DropResult } from 'react-beautiful-dnd';

// Type for drag handlers
type DragHandler = (result: DropResult) => void;

interface DragContextType {
  registerHandler: (droppableId: string, handler: DragHandler) => void;
  unregisterHandler: (droppableId: string) => void;
  handleDrag: (result: DropResult) => void;
}

const DragContext = createContext<DragContextType | null>(null);

interface DragProviderProps {
  children: ReactNode;
}

export const DragProvider: React.FC<DragProviderProps> = ({ children }) => {
  const [handlers, setHandlers] = useState<Map<string, DragHandler>>(new Map());

  const registerHandler = useCallback((droppableId: string, handler: DragHandler) => {
    setHandlers(prev => {
      const newMap = new Map(prev);
      newMap.set(droppableId, handler);
      return newMap;
    });
  }, []);

  const unregisterHandler = useCallback((droppableId: string) => {
    setHandlers(prev => {
      const newMap = new Map(prev);
      newMap.delete(droppableId);
      return newMap;
    });
  }, []);

  const handleDrag = useCallback((result: DropResult) => {
    const { destination } = result;
    
    if (!destination) {
      return;
    }

    const droppableId = destination.droppableId;
    
    // Find the appropriate handler
    let handler: DragHandler | undefined;
    
    if (droppableId === 'tests-grid') {
      handler = handlers.get('tests-grid');
    } else if (droppableId.startsWith('questions-list-')) {
      // For questions, find the handler by exact droppableId match
      handler = handlers.get(droppableId);
    }

    if (handler) {
      handler(result);
    } else {
      console.warn(`No drag handler found for droppableId: ${droppableId}`);
    }
  }, [handlers]);

  const value = {
    registerHandler,
    unregisterHandler,
    handleDrag,
  };

  return (
    <DragContext.Provider value={value}>
      {children}
    </DragContext.Provider>
  );
};

export const useDrag = (): DragContextType => {
  const context = useContext(DragContext);
  if (!context) {
    throw new Error('useDrag must be used within a DragProvider');
  }
  return context;
};